import * as turf from '@turf/turf';
import axios from 'axios';

const baseApiUrl = import.meta.env.VITE_ENC_API_URL_PREFIX;
const apiToken = import.meta.env.VITE_VECTOR_CHARTS_API_TOKEN;

export const ELEVATION_OPTIONS = {
    tileZoom: 14,
    filterOutputReferences: ['MSL', 'HAE'],
    outputUnit: 'meters',
};

function responseError(message) {
    const error = new Error(`Invalid elevation response: ${message}`);
    error.code = 'invalid_elevation_response';
    return error;
}

function normalizeFailure(failure) {
    return {
        index: failure.index,
        code: failure.code || 'unknown_failure',
        message: failure.message || 'Elevation lookup failed.',
    };
}

function normalizeShape(shape) {
    const locations = shape?.locations;
    const elevations = shape?.elevations;
    if (!Array.isArray(locations) || !Array.isArray(elevations)) {
        throw responseError('each result must include locations and elevations');
    }

    const bands = elevations.map((band) => {
        const reference = band?.reference?.toUpperCase();
        const values = band?.values;
        if (!reference || !Array.isArray(values)) {
            throw responseError('each elevation band must include a reference and values');
        }
        if (values.length !== locations.length) {
            throw responseError('elevation bands must match the number of locations');
        }
        return { reference, values };
    });
    if (!bands.length) {
        throw responseError('each result must include at least one elevation band');
    }
    if (new Set(bands.map((band) => band.reference)).size !== bands.length) {
        throw responseError('elevation references must be unique');
    }

    const failuresByIndex = new Map(
        (shape.failures || []).map((failure) => [
            failure.index,
            normalizeFailure(failure),
        ]),
    );

    return locations.map((location, index) => {
        if (
            !location ||
            !Number.isFinite(location.latitude) ||
            !Number.isFinite(location.longitude)
        ) {
            throw responseError(`location ${index} is invalid`);
        }

        const failure = failuresByIndex.get(index) || null;
        const sampleElevations = Object.fromEntries(
            bands.map(({ reference, values }) => [reference, values[index]]),
        );
        if (
            !Object.values(sampleElevations).some(Number.isFinite) &&
            !failure
        ) {
            throw responseError(`missing failure detail for sample ${index}`);
        }
        if (
            Object.values(sampleElevations).some(
                (value) => value != null && !Number.isFinite(value),
            )
        ) {
            throw responseError(`elevation values for sample ${index} are invalid`);
        }

        return {
            latitude: location.latitude,
            longitude: location.longitude,
            elevations: sampleElevations,
            failure,
        };
    });
}

function addDistanceAlong(samples) {
    let distanceAlong = 0;
    return samples.map((sample, index) => {
        if (index > 0) {
            const previous = samples[index - 1];
            distanceAlong +=
                turf.distance(
                    [previous.longitude, previous.latitude],
                    [sample.longitude, sample.latitude],
                    { units: 'kilometers' },
                ) * 1000;
        }
        return {
            ...sample,
            distanceAlong,
            distance_along: distanceAlong,
            height_msl: sample.elevations.MSL,
        };
    });
}

async function requestElevation(path, body) {
    const response = await axios.post(
        `${baseApiUrl}${path}?token=${apiToken}`,
        body,
    );
    const data = response.data?.data;
    if (!data || (Array.isArray(data) && data.length === 0)) {
        throw responseError('data must include at least one result');
    }
    return {
        unit: response.data?.metadata?.unit || 'meters',
        shapes: (Array.isArray(data) ? data : [data]).map(normalizeShape),
    };
}

export async function getPointElevations(coordinates) {
    const result = await requestElevation('/api/v2/elevation/lookup/points', {
        coordinates,
        options: ELEVATION_OPTIONS,
    });
    return {
        unit: result.unit,
        samples: addDistanceAlong(result.shapes[0] || []),
    };
}

export async function getPolylineElevations(polylines) {
    const result = await requestElevation('/api/v2/elevation/lookup/polylines', {
        polylines,
        options: ELEVATION_OPTIONS,
    });
    if (result.shapes.length !== polylines.length) {
        throw responseError('result count does not match the requested polylines');
    }
    return {
        unit: result.unit,
        profiles: result.shapes.map(addDistanceAlong),
    };
}
